import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { isAppLocale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { authErrorMessage } from "@/lib/auth-error";
import { tryLegacyPassword } from "@/lib/legacy-password";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

type AcceptState = "idle" | "accepting" | "success" | "error";

export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const token = searchParams.get("token");

  const [state, setState] = useState<AcceptState>("idle");
  // Named for what it holds rather than `errorMessage`, which is now the shared mapper.
  const [failureMessage, setFailureMessage] = useState("");

  // Auto-accept when user is authenticated and token is present
  useEffect(() => {
    if (!token || !session?.access_token || state !== "idle") return;

    setState("accepting");

    fetch(`${API_URL}/api/v1/invitations/accept`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        // One reader for both wire shapes, so POS2-1152's move to the `AppError` envelope is a
        // change in `lib/api-error.ts` rather than at every call site (POS2-1134 CP2).
        if (!res.ok) throw await apiError(t, res);
        return res.json();
      })
      .then(async () => {
        setState("success");
        toast.success(t("auth.inviteAccepted"));

        // Refresh the session so JWT claims reflect the new org membership
        await supabase.auth.refreshSession();

        // Short delay so the user sees the success state before redirect
        setTimeout(() => navigate("/", { replace: true }), 1500);
      })
      .catch((err: Error) => {
        setState("error");
        setFailureMessage(err.message);
      });
  }, [token, session?.access_token, state, navigate, t]);

  if (!token) {
    return (
      <CenteredCard title={t("auth.inviteInvalidTitle")}>
        <p className="text-sm text-muted-foreground">{t("auth.inviteInvalidDescription")}</p>
      </CenteredCard>
    );
  }

  // Loading auth state
  if (isLoading) {
    return (
      <CenteredCard title={t("common.loading")}>
        <p className="text-sm text-muted-foreground">{t("auth.invitePleaseWait")}</p>
      </CenteredCard>
    );
  }

  // Not signed in — show inline sign-in options
  if (!user) {
    return <SignInToAccept />;
  }

  if (state === "accepting" || state === "idle") {
    return (
      <CenteredCard title={t("auth.inviteAcceptingTitle")}>
        <p className="text-sm text-muted-foreground">{t("auth.inviteAcceptingDescription")}</p>
      </CenteredCard>
    );
  }

  if (state === "success") {
    return (
      <CenteredCard title={t("auth.inviteSuccessTitle")}>
        <p className="text-sm text-muted-foreground">{t("auth.inviteSuccessDescription")}</p>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard title={t("auth.inviteFailedTitle")}>
      <p className="text-sm text-destructive">{failureMessage}</p>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate("/login", { replace: true })}>
          {t("auth.inviteSignInDifferent")}
        </Button>
        <Button size="sm" onClick={() => setState("idle")}>
          {t("auth.inviteTryAgain")}
        </Button>
      </div>
    </CenteredCard>
  );
}

function SignInToAccept() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // The organisation's language, put on the link by the invitation email (POS2-1160). GoTrue's
  // confirmation email can read nothing but the new user's own `raw_user_meta_data`, so the locale
  // has to be written at signup — and nothing here can see the organisation, since the page holds
  // only the token and there is no public by-token read. Narrowed at this boundary because the
  // value came off a URL: a locale the app does not ship must not reach the metadata a Go template
  // then compares against.
  const invitedLang = searchParams.get("lang");
  const invitedLocale = invitedLang && isAppLocale(invitedLang) ? invitedLang : null;
  // The DataBridge migration invitation marks its link, because its recipient already has a
  // password — POS2-1309 carries it over on first login. Read off the URL for the same reason
  // `lang` is: the page holds only a token and there is no public by-token read, so it cannot ask
  // the server anything about the invitation it is showing. Anything but the one marker means the
  // default, so a hand-edited value lands on create-account rather than on nothing (POS2-1332).
  const migrated = searchParams.get("mode") === "signin";
  // Invitees don't have an account yet, so default to create-account; the toggle below
  // lets an already-registered invitee switch to sign-in (POS2-997, was "signin" POS2-693).
  const [mode, setMode] = useState<"signin" | "signup">(migrated ? "signin" : "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState("");

  /**
   * Sign in, and carry an old-platform password over if that is what was typed (POS2-1309).
   *
   * **This screen matters more than the login page for a carried-over user**, because it is where
   * our own invitation email sends them. Without the fallback, somebody who switched to sign-in and
   * entered their correct old FoodSight password got a plain "credentials do not match" — the one
   * outcome this ticket exists to prevent, on the one screen they were most likely to see it.
   *
   * Same single boolean as the login page, and for the same reason: there is nothing to branch on
   * but "it worked", so this screen cannot report whether an address was carried over either.
   */
  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (!error) return;

    if (error.code === "invalid_credentials" && (await tryLegacyPassword(email, password))) {
      const retry = await supabase.auth.signInWithPassword({ email, password });
      if (!retry.error) return;
      toast.error(authErrorMessage(t, retry.error));
      setLoading(false);
      return;
    }

    toast.error(
      authErrorMessage(t, error),
      error.code === "invalid_credentials"
        ? { description: t("auth.legacyPasswordHint") }
        : undefined,
    );
    setLoading(false);
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordsDoNotMatch"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.href, data: { locale: invitedLocale } },
    });

    setLoading(false);

    if (error) {
      // **Already registered — move them to sign-in with the address they typed still in the box.**
      //
      // The trigger is GoTrue's own `user_already_exists`, produced by their own attempt, so this
      // reveals nothing it has not already said. Asking the server "does this address have an
      // account" *before* they tried would be the enumeration oracle, and is deliberately not
      // what this does: no lookup, no endpoint, no answer to anybody who has not just proved they
      // hold the password they are about to type.
      //
      // The sign-in form they land on carries the legacy fallback above, so a carried-over user
      // whose account exists resolves in one more click rather than being told to reset.
      if (error.code === "email_exists" || error.code === "user_already_exists") {
        setMode("signin");
        setPassword("");
        setConfirmPassword("");
        toast.info(t("auth.inviteAlreadyRegistered"));
        return;
      }
      toast.error(authErrorMessage(t, error));
      return;
    }

    // If Supabase returned a session, the user was auto-confirmed.
    // The auth store will update and the parent component will accept the invitation.
    if (data.session) return;

    // No session — email confirmation required. Show verification page.
    setSignedUpEmail(email);
  }

  async function handleResendVerification() {
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: signedUpEmail,
      options: { emailRedirectTo: window.location.href },
    });
    setLoading(false);

    if (error) {
      toast.error(authErrorMessage(t, error));
    } else {
      toast.success(t("auth.inviteVerificationSent"));
    }
  }

  async function handleGoogleSignIn() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.href,
      },
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
    }
  }

  if (signedUpEmail) {
    return (
      <CenteredCard title={t("auth.inviteCheckEmailTitle")}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {t("auth.inviteCheckEmailPrefix")} <strong>{signedUpEmail}</strong>.{" "}
            {t("auth.inviteCheckEmailSuffix")}
          </p>
          <Button variant="outline" size="sm" onClick={handleResendVerification} disabled={loading}>
            {loading ? t("auth.forgotSending") : t("auth.inviteResendVerification")}
          </Button>
        </div>
      </CenteredCard>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-xl">
            {mode === "signup" ? t("auth.inviteCreateAccountTitle") : t("auth.inviteSignInTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {mode === "signup"
              ? t("auth.inviteCreateAccountDescription")
              : migrated
                ? t("auth.inviteMigratedDescription")
                : t("auth.inviteSignInDescription")}
          </p>

          <Button type="button" variant="outline" onClick={handleGoogleSignIn} className="w-full">
            {t("auth.continueWithGoogle")}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                {t("auth.orContinueWithEmail")}
              </span>
            </div>
          </div>

          <form
            onSubmit={mode === "signup" ? handleSignUp : handleSignIn}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                {t("auth.email")}
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm font-medium">
                {t("auth.password")}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
            {mode === "signup" && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="confirm-password" className="text-sm font-medium">
                  {t("auth.confirmPassword")}
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full">
              {loading
                ? mode === "signup"
                  ? t("auth.creatingAccount")
                  : t("auth.signingIn")
                : mode === "signup"
                  ? t("auth.createAccount")
                  : t("auth.signIn")}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {mode === "signup" ? (
              <>
                {t("auth.alreadyHaveAccount")}{" "}
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {t("auth.signIn")}
                </button>
              </>
            ) : (
              <>
                {t("auth.newHere")}{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {t("auth.createAnAccount")}
                </button>
              </>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function CenteredCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-xl">{title}</CardTitle>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
